import type { RoadmapItem, Section, StoredProductBoardUi } from './product-board-types';
import { PRODUCT_BOARD_UI_STORAGE_KEY } from './product-board-types';

// Mock data parsed from actual markdown files
// In real implementation, this would be fetched from backend API
export const mockData = {
  currentCycle: {
    number: 1,
    theme: 'System initialization and foundation cleanup',
    startDate: '2025-01-06',
    endDate: '2025-01-19',
    status: 'planning',
    daysTotal: 10,
    daysUsed: 1,
    bufferDays: 2,
    progress: 80,
    bigBets: [
      {
        id: 'prod-mgmt-setup',
        title: 'Product Management System Setup',
        appetite: 'big',
        status: 'complete',
        progress: 100,
        owner: 'Rowan',
        mustHaves: {
          completed: 9,
          total: 10
        }
      }
    ],
    mediumBets: [],
    smallBets: [
      {
        id: 'system-validation',
        title: 'System Validation',
        progress: 0,
        tasks: [
          { text: 'Use product management system', completed: false },
          { text: 'Gather feedback on workflow', completed: false },
          { text: 'Adjust templates as needed', completed: false }
        ]
      }
    ],
    blockers: [],
    risks: [
      {
        text: 'System too complex for solo dev',
        impact: 'medium',
        status: 'watching'
      }
    ]
  },
  roadmap: {
    now: [
      {
        id: 'prod-mgmt',
        title: 'Documentation Cleanup & Product Management Setup',
        type: 'big',
        appetite: '2 weeks',
        progress: 80,
        status: 'in_progress',
        lastWorked: '2026-02-27',
        nextSteps: 'First cycle, workflow guide',
        path: 'docs/product/bets/2026-01-03-product-management-extension.md',
      }
    ],
    next: [
      {
        id: 'product-board-v1',
        title: 'Product Board v1 — Connect & Ship',
        type: 'small',
        appetite: '1 week',
        status: 'shaped',
        priority: 'high',
        value: 'Wire prototype to live API, drag-to-chat, Open in Editor, sidebar route.',
        path: 'docs/product/bets/2026-03-01-product-board-v1-connect-and-ship.md',
      },
      {
        id: 'product-mgmt-ext',
        title: 'Product Management Extension',
        type: 'big',
        appetite: '2 weeks',
        status: 'shaped',
        priority: 'high',
        value: 'Visual dashboard, roadmap view, progress tracking - makes product mgmt system actually usable!',
        path: 'docs/product/bets/2026-01-03-product-management-extension.md',
      },
      {
        id: 'tab-chrome',
        title: 'Tab System Chrome Redesign Completion',
        type: 'medium',
        appetite: '1 week',
        status: 'shaped',
        priority: 'high',
      },
      {
        id: 'file-mgmt',
        title: 'File Management Improvements',
        type: 'medium',
        appetite: '1 week',
        status: 'needs-shaping',
        priority: 'high',
      },
      {
        id: 'basecamp-chat',
        title: 'Basecamp Chat Integration Enhancements',
        type: 'medium',
        appetite: '1 week',
        status: 'partially-shaped',
        priority: 'medium',
      },
      {
        id: 'daily-digest',
        title: 'Daily Digest - Phase 1 (Text Summaries)',
        type: 'small',
        appetite: '2-3 days',
        status: 'shaped',
        priority: 'medium',
        dependencies: ['product-mgmt-ext'],
      }
    ],
    later: [
      {
        id: 'ai-agent-system',
        title: 'AI Agent System',
        category: 'strategic',
        items: ['AI Agents as Extensions', 'Assign to Agent Widget']
      },
      {
        id: 'ui-improvements',
        title: 'UI/UX Improvements',
        items: ['Command palette enhancements', 'Keyboard shortcuts system', 'Theme customization']
      },
      {
        id: 'integrations',
        title: 'Integrations & Extensions',
        items: ['Asana integration', 'GitHub integration', 'Multi-User Communication & Notification Layer']
      }
    ]
  }
};

/** Active cloud dispatch row from GET /api/cursor-cloud/active-dispatches */
export type ActiveCloudDispatchRow = {
  agentId?: string;
  title?: string;
  dispatchedAt?: string;
  prUrl?: string | null;
  filePath?: string;
  documentId?: string;
  threadId?: string | null;
  dispatchStatus?: string;
};

export type CloudDispatchResultEntry = {
  agentId: string;
  title: string;
  dispatchedAt: string;
  prUrl?: string | null;
  error?: string;
  inboxItemId?: string | null;
  threadId?: string | null;
  dispatchStatus?: string;
  isActive?: boolean;
};

/** Only bets that can show cloud dispatch UI should hit dispatch-for-document. */
export function isRoadmapItemCloudDispatchCandidate(
  item: Pick<
    RoadmapItem,
    'documentId' | 'type' | 'status' | 'safeToDispatch' | 'needsShapingFirst' | 'dispatchTarget'
  >
): boolean {
  if (!item.documentId?.trim()) return false;
  if (item.type && item.type !== 'bet') return false;
  if (item.safeToDispatch === false) return false;
  if (item.needsShapingFirst) return false;
  if (item.dispatchTarget && item.dispatchTarget !== 'cursor-cloud') return false;
  const status = item.status ?? '';
  if (['complete', 'shaping', 'shaped'].includes(status)) return false;
  return true;
}

function betPathAliases(p: string | undefined): Set<string> {
  const out = new Set<string>();
  if (!p?.trim()) return out;
  const t = p.trim();
  out.add(t);
  const norm = normalizeBetDocFilePath(t);
  if (norm) out.add(norm);
  const raw = getRawBetFilePath(t);
  if (raw) {
    out.add(raw);
    const rawNorm = normalizeBetDocFilePath(raw);
    if (rawNorm) out.add(rawNorm);
  }
  return out;
}

/** Match an active dispatch (by DB file_path or document id) to a roadmap card id. */
export function findRoadmapItemIdForDispatch(
  sections: Section[],
  filePath?: string | null,
  documentId?: string | null
): string | null {
  const dispatchPaths = betPathAliases(filePath ?? undefined);
  const docId = documentId?.trim() || null;
  for (const section of sections) {
    for (const item of section.items) {
      if (docId && item.documentId?.trim() === docId) return item.id;
      if (dispatchPaths.size > 0 && item.path) {
        const itemPaths = betPathAliases(item.path);
        for (const dp of dispatchPaths) {
          if (itemPaths.has(dp)) return item.id;
        }
      }
      if (dispatchPaths.size > 0 && item.handoffPath) {
        const itemHandoffPaths = betPathAliases(item.handoffPath);
        for (const dp of dispatchPaths) {
          if (itemHandoffPaths.has(dp)) return item.id;
        }
      }
    }
  }
  return null;
}

/** Merge API active-dispatches into product-board state (keyed by roadmap item id). */
export function mergeActiveDispatchesIntoResult(
  sections: Section[],
  dispatches: ActiveCloudDispatchRow[],
  prev: Record<string, CloudDispatchResultEntry>
): Record<string, CloudDispatchResultEntry> {
  const next = { ...prev };
  for (const d of dispatches) {
    const agentId = d.agentId?.trim();
    if (!agentId) continue;
    const itemId = findRoadmapItemIdForDispatch(sections, d.filePath, d.documentId);
    if (!itemId) continue;
    const existing = next[itemId];
    next[itemId] = {
      agentId,
      title: d.title || existing?.title || 'Cloud agent',
      dispatchedAt: d.dispatchedAt || existing?.dispatchedAt || '',
      prUrl: d.prUrl ?? existing?.prUrl ?? null,
      threadId: d.threadId ?? existing?.threadId ?? null,
      dispatchStatus: d.dispatchStatus ?? existing?.dispatchStatus ?? 'running',
      isActive:
        d.dispatchStatus != null
          ? d.dispatchStatus === 'running' ||
            !['complete', 'error', 'cancelled', 'failed', 'timeout'].includes(d.dispatchStatus)
          : (existing?.isActive ?? true),
      inboxItemId: existing?.inboxItemId ?? null,
      error: existing?.error,
    };
  }
  return next;
}

/** Maps roadmap-relative paths to DB keys used by documents API (app repo docs/). */
export function normalizeBetDocFilePath(p: string | undefined): string | undefined {
  if (!p) return undefined;
  if (p.startsWith('flownotes-app-docs/')) return p;
  if (p.startsWith('docs/')) return `flownotes-app-docs/${p.slice(5)}`;
  return p;
}

/** Prefer `documents.id` UUID for chat chips and getDocument; fallback to roadmap slug if not synced. */
export function betDocumentReferenceId(item: Pick<RoadmapItem, 'id' | 'documentId'>): string {
  const uid = item.documentId?.trim();
  return uid || item.id;
}

/** Returns the human-readable relative path (docs/…) for clipboard copy. */
export function getRawBetFilePath(p: string | undefined): string | undefined {
  if (!p) return undefined;
  if (p.startsWith('flownotes-app-docs/')) return `docs/${p.slice('flownotes-app-docs/'.length)}`;
  if (p.startsWith('docs/')) return p;
  return p;
}

/** Returns just the filename from a bet path. */
export function getBetFileName(p: string | undefined): string | undefined {
  const raw = getRawBetFilePath(p);
  if (!raw) return undefined;
  return raw.split('/').pop();
}

/**
 * Generate a preview from a RoadmapItem's own metadata when no bet file is linked.
 * Used for LATER/NEXT ideas that haven't been shaped into a dedicated bet doc yet.
 */
export function generateItemPreviewMarkdown(item: RoadmapItem): string {
  const lines: string[] = [`# ${item.title}`, ''];
  if (item.status) lines.push(`**Status:** ${item.status}`);
  if (item.appetite) lines.push(`**Appetite:** ${item.appetite}`);
  if (item.owner) lines.push(`**Owner:** ${item.owner}`);
  lines.push('', '---', '');
  lines.push('*No bet document linked yet — this item is listed in the roadmap without a dedicated bet file.*');
  if (item.items && item.items.length > 0) {
    lines.push('', '## Notes', '');
    for (const sub of item.items) lines.push(`- ${sub}`);
  }
  return lines.join('\n');
}

/** Mock markdown for bet preview when only prototype mock IDs exist (no file path). */
export function getBetMarkdownMock(betId: string): string {
  const bet = mockData.currentCycle.bigBets.find(b => b.id === betId);
  if (!bet) {
    return [
      '# Bet not found',
      '',
      'No linked bet file for this item. Open the bet from the roadmap once it lists a file path, or from the repo.',
    ].join('\n')
  }

  return `# ${bet.title}

**Shaped Date:** 2025-01-03
**Status:** ${bet.status}
**Appetite:** ${bet.appetite}

---

## 🎯 Problem

Product management system exists as markdown files but lacks visual overview and quick access to status information.

**Pain Points:**
- "I'm overwhelmed by loose files" (user quote)
- Can't see priorities at a glance
- Hard to get quick status overview

---

## 💡 Solution

Build a visual dashboard that:
- Shows current cycle status at a glance
- Displays progress with visual indicators
- Provides quick actions for common tasks
- Links to detailed markdown files for editing

---

## ✅ Must-Haves

${bet.mustHaves.completed}/${bet.mustHaves.total} completed

- [x] Fix incorrectly dated files
- [x] Archive completed specs
- [x] Create CANONICAL-INDEX.md
- [x] Design hybrid product management system
- [x] Create product folder structure
- [x] Create templates (cycle, bet, experiment, roadmap)
- [ ] Initialize first real cycle plan
- [x] Populate NOW/NEXT/LATER roadmaps with actual work
- [x] Create product management workflow guide
- [x] Trial run with first cycle

---

## 🎯 Success Criteria

**How we know it worked (measurable):**

1. **Functional:**
   - ✅ Clear documentation structure
   - ✅ Reduced root specs from 234 → 169 files
   - ✅ Templates ready to use
   - ⏳ System validated through first real cycle

2. **User Value:**
   - User reports "less overwhelmed" by files
   - User can get status overview in < 5 seconds
   - User prefers UI over opening markdown files

---

*This is a preview. Click "Open" to edit the full bet document in the main editor.*`;
}

/**
 * @deprecated Use `section.sourceFilePath` from the API response instead.
 * DB file_path for NOW/NEXT/LATER/DONE index files (app repo docs via multi-root mount).
 * This function is kept for backward compatibility but will be removed in a future version.
 */
export function getRoadmapColumnSourcePath(sectionId: string): string | null {
  switch (sectionId) {
    case 'now':
      return 'flownotes-app-docs/product/roadmap/NOW.md';
    case 'next':
      return 'flownotes-app-docs/product/roadmap/NEXT.md';
    case 'later':
      return 'flownotes-app-docs/product/roadmap/LATER.md';
    case 'done':
      return 'flownotes-app-docs/product/roadmap/DONE.md';
    default:
      return null;
  }
}

export function loadProductBoardUiFromStorage(): StoredProductBoardUi | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PRODUCT_BOARD_UI_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as StoredProductBoardUi;
    if (o.v !== 1) return null;
    return o;
  } catch {
    return null;
  }
}

export function saveProductBoardUiToStorage(payload: {
  currentView: string;
  sidePanelView: string | null;
  sidePanelBetId: string | null;
  sidePanelRoadmapPath: string | null;
  filtersExpanded?: boolean;
}) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PRODUCT_BOARD_UI_STORAGE_KEY, JSON.stringify({ v: 1, ...payload }));
  } catch {
    /* quota */
  }
}

/** MCP resolves `documents.file_path` — use `flownotes-app-docs/...`, not bare `docs/...`. */
export function buildMcpPathSnippet(path: string | undefined, organizationId?: string | null): string {
  const db = normalizeBetDocFilePath(path);
  if (!db) return '';
  const oid = organizationId?.trim();
  const orgJson = oid ? `, "_organizationId": "${oid}"` : '';
  return `flownotes_get_document_by_path({ "filePath": "${db}", "includeContent": true${orgJson} })`;
}

export function buildDelegatePrompt(item: { title: string; path?: string }, organizationId?: string | null): string {
  const db = normalizeBetDocFilePath(item.path);
  const human = getRawBetFilePath(item.path);
  const oid = organizationId?.trim();
  const cloudMcp = oid
    ? `Maater Cloud MCP is org-scoped: use the same "_organizationId" (or organizationId) as in Copy MCP lookup (${oid}) on get_document_by_path and related tools. Local stdio MCP omits it.`
    : 'If using Maater Cloud MCP (not local stdio), pass your workspace UUID as _organizationId or organizationId on the same tool call so lookups hit the right org; local MCP omits it.';
  const mcp = db
    ? [
        `Load bet via MCP first: flownotes_get_document_by_path with filePath "${db}" (includeContent: true). That string is the DB documents.file_path key for this bet.`,
        human && human !== db
          ? `Same file in the Maater app repo: ${human}. If MCP returns "Document not found", Read that repo path (watcher/sync may lag) then retry MCP after sync.`
          : 'If MCP returns "Document not found", confirm the bet is synced into documents (file watcher / multi-root app docs).',
        cloudMcp,
      ].join('\n')
    : ['No bet file path is linked; add a Reference/Source line in NOW/NEXT.md first.', cloudMcp].join('\n');
  return [
    `Delegate: ${item.title}`,
    '',
    mcp,
    '',
    'Use the create-handoff skill (.cursor/skills/create-handoff/SKILL.md).',
    'When done, follow doc-update-on-completion (.cursor/rules/doc-update-on-completion.mdc) and documentation-management bet cycling.',
  ].join('\n');
}

/** Statuses that block cloud dispatch entirely (complete = done, no work needed). */
const DISPATCH_BLOCKED_STATUSES = ['complete'];
/** Statuses that indicate the bet needs shaping before dispatch. */
const SHAPING_STATUSES = ['shaping', 'shaped'];
/** dispatch_target values that mean cloud dispatch is not applicable. */
const NON_CLOUD_DISPATCH_TARGETS = ['inline', 'human-only', 'human'];

/** True when the bet is ready to dispatch to a cloud agent. */
export function isDispatchReady(item: RoadmapItem): boolean {
  const s = item.status ?? '';
  if (DISPATCH_BLOCKED_STATUSES.includes(s)) return false;
  if (SHAPING_STATUSES.includes(s)) return false;
  if (item.needsShapingFirst) return false;
  if (item.safeToDispatch === false) return false;
  if (item.dispatchTarget && NON_CLOUD_DISPATCH_TARGETS.includes(item.dispatchTarget)) return false;
  return true;
}

/** True when the bet is in a shaping state — show "Shape in chat" CTA instead of dispatch. */
export function isShapingNeeded(item: RoadmapItem): boolean {
  return SHAPING_STATUSES.includes(item.status ?? '') || item.needsShapingFirst === true;
}

/** True when dispatch is blocked for a non-shaping, non-complete reason (show disabled button + tooltip). */
export function isDispatchBlocked(item: RoadmapItem): boolean {
  const s = item.status ?? '';
  if (DISPATCH_BLOCKED_STATUSES.includes(s)) return false; // complete: show badge, no dispatch
  if (isShapingNeeded(item)) return false; // shaping: show Shape CTA instead
  return item.safeToDispatch === false ||
    (!!item.dispatchTarget && NON_CLOUD_DISPATCH_TARGETS.includes(item.dispatchTarget));
}

/** Pilot critical-path tag used in roadmap YAML and bet frontmatter */
export const MVP_CRITICAL_TAG = 'mvp-critical';

export type BoardCycleFilter = 'all' | 'none' | number;

export type ProductBoardFilters = {
  tagConjunction: string[];
  mvpCriticalOnly: boolean;
  dispatchableNowOnly: boolean;
  cycle: BoardCycleFilter;
};

/**
 * Product Board "Dispatchable now" filter (bet: 2026-05-08-product-board-cycle-mvp-lane).
 * Intentionally stricter than card dispatch button visibility (isDispatchReady).
 */
export function isDispatchableNowFilter(item: RoadmapItem): boolean {
  if (item.dispatchTarget !== 'cursor-cloud') return false;
  if (item.safeToDispatch !== true) return false;
  if (item.needsShapingFirst === true) return false;
  const s = item.status ?? '';
  return s === 'shaping' || s === 'shaped' || s === 'in_progress';
}

export function roadmapItemMatchesBoardFilters(item: RoadmapItem, f: ProductBoardFilters): boolean {
  if (f.dispatchableNowOnly && !isDispatchableNowFilter(item)) return false;
  if (f.mvpCriticalOnly) {
    const tags = item.tags ?? [];
    if (!tags.includes(MVP_CRITICAL_TAG)) return false;
  }
  for (const t of f.tagConjunction) {
    const tags = item.tags ?? [];
    if (!tags.includes(t)) return false;
  }
  if (f.cycle !== 'all') {
    if (f.cycle === 'none') {
      if (item.cycle != null && !Number.isNaN(item.cycle)) return false;
    } else if (item.cycle !== f.cycle) {
      return false;
    }
  }
  return true;
}

export function collectUnionTagsFromSections(sections: Section[]): string[] {
  const set = new Set<string>();
  for (const sec of sections) {
    for (const it of sec.items) {
      for (const t of it.tags ?? []) {
        if (t) set.add(t);
      }
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Tag + count for roadmap board filters (excludes MVP_CRITICAL_TAG; it has its own control). */
export type BoardTagFilterEntry = { tag: string; count: number };

export function sortedBoardTagFilterEntries(sections: Section[]): BoardTagFilterEntry[] {
  const m = new Map<string, number>();
  for (const sec of sections) {
    for (const it of sec.items) {
      for (const t of it.tags ?? []) {
        if (!t) continue;
        m.set(t, (m.get(t) ?? 0) + 1);
      }
    }
  }
  const entries: BoardTagFilterEntry[] = [];
  for (const [tag, count] of m) {
    if (tag === MVP_CRITICAL_TAG) continue;
    entries.push({ tag, count });
  }
  entries.sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
  return entries;
}

export function splitBoardTagFiltersInlineVsOverflow(
  sortedEntries: BoardTagFilterEntry[],
  selectedTags: string[],
  maxInline: number
): { inline: BoardTagFilterEntry[]; overflow: BoardTagFilterEntry[] } {
  const selected = new Set(selectedTags);
  const selectedOrdered = sortedEntries.filter((e) => selected.has(e.tag));
  const unselectedOrdered = sortedEntries.filter((e) => !selected.has(e.tag));
  const inline: BoardTagFilterEntry[] = [];
  const inlineSet = new Set<string>();
  for (const e of selectedOrdered) {
    if (inline.length >= maxInline) break;
    inline.push(e);
    inlineSet.add(e.tag);
  }
  for (const e of unselectedOrdered) {
    if (inline.length >= maxInline) break;
    inline.push(e);
    inlineSet.add(e.tag);
  }
  const overflow = sortedEntries.filter((e) => !inlineSet.has(e.tag));
  return { inline, overflow };
}

export function countItemsWithMvpCriticalTagInSections(sections: Section[]): number {
  let n = 0;
  for (const sec of sections) {
    for (const it of sec.items) {
      if ((it.tags ?? []).includes(MVP_CRITICAL_TAG)) n++;
    }
  }
  return n;
}

export function countDispatchableNowFilterItemsInSections(sections: Section[]): number {
  let n = 0;
  for (const sec of sections) {
    for (const it of sec.items) {
      if (isDispatchableNowFilter(it)) n++;
    }
  }
  return n;
}

/** Cycle dropdown rows after "All cycles", sorted by count descending (ties by label). */
export type BoardCycleSelectRow = { value: string; label: string; count: number };

export function sortedCycleSelectRowsFromSections(sections: Section[]): BoardCycleSelectRow[] {
  const flat = sections.flatMap((s) => s.items);
  let noneCount = 0;
  const byNum = new Map<number, number>();
  for (const it of flat) {
    if (it.cycle == null || Number.isNaN(it.cycle)) noneCount++;
    else byNum.set(it.cycle, (byNum.get(it.cycle) ?? 0) + 1);
  }
  const rows: BoardCycleSelectRow[] = [
    { value: 'none', label: 'No cycle', count: noneCount },
    ...[...byNum.entries()].map(([num, count]) => ({
      value: String(num),
      label: `Cycle ${num}`,
      count,
    })),
  ];
  rows.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return rows.map(({ value, label, count }) => ({
    value,
    label: `${label} (${count})`,
    count,
  }));
}

export function collectUnionCyclesFromSections(sections: Section[]): number[] {
  const set = new Set<number>();
  for (const sec of sections) {
    for (const it of sec.items) {
      if (it.cycle != null && !Number.isNaN(it.cycle)) set.add(it.cycle);
    }
  }
  return [...set].sort((a, b) => a - b);
}

/** Human-readable bet size label for dashboard cards from appetite string. */
export function dashboardBetSizeLabel(item: Pick<RoadmapItem, 'appetite'>): string {
  const a = (item.appetite ?? '').toLowerCase();
  if (a.includes('big') || /\d+[\s-]*week/.test(a)) return 'Big Bet';
  if (a.includes('small') || a.includes('day') || a.includes('half')) return 'Small Bet';
  return 'Medium Bet';
}

export function dashboardBetSizeBadgeClass(label: string): string {
  if (label === 'Big Bet') return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
  if (label === 'Small Bet') return 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20';
  return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
}

export function roadmapItemMatchesSearch(item: RoadmapItem, query: string): boolean {
  const t = query.trim().toLowerCase();
  if (!t) return true;
  const blob = [
    item.title,
    item.owner,
    item.lastWorked,
    item.nextSteps,
    item.value,
    item.path,
    item.appetite,
    item.status !== undefined && item.status !== null ? String(item.status) : '',
    item.type,
  ]
    .filter((s) => typeof s === 'string' && s.length > 0)
    .join(' ')
    .toLowerCase();
  return blob.includes(t);
}

/** Empty columns until GET /api/roadmap returns (no mock placeholder cards). */
export const emptyRoadmapSections: Section[] = [
  {
    id: 'now',
    title: 'NOW',
    color: 'green',
    badge: `Cycle ${mockData.currentCycle.number}`,
    items: [],
  },
  {
    id: 'next',
    title: 'NEXT',
    color: 'blue',
    badge: 'Shaped & Ready',
    items: [],
  },
  {
    id: 'later',
    title: 'LATER',
    color: 'gray',
    badge: 'Backlog',
    items: [],
  },
  {
    id: 'done',
    title: 'DONE',
    color: 'purple',
    badge: 'Completed',
    items: [],
  },
];

/** Only used when /api/roadmap fails (offline / server down). */
export const mockRoadmapSectionsFallback: Section[] = [
  {
    id: 'now',
    title: 'NOW',
    color: 'green',
    badge: `Cycle ${mockData.currentCycle.number}`,
    items: mockData.roadmap.now as RoadmapItem[],
  },
  {
    id: 'next',
    title: 'NEXT',
    color: 'blue',
    badge: 'Shaped & Ready',
    items: mockData.roadmap.next as RoadmapItem[],
  },
  {
    id: 'later',
    title: 'LATER',
    color: 'gray',
    badge: 'Backlog',
    items: mockData.roadmap.later as RoadmapItem[],
  },
  {
    id: 'done',
    title: 'DONE',
    color: 'purple',
    badge: 'Completed',
    items: [],
  },
];
